import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { buildCookie, otherChannel, UiChannel } from './ui-channel';

/**
 * Fassade über dem Entscheidungskern in {@link ./ui-channel} (#741).
 *
 * Hält keinen eigenen Zustand: welcher Kanal ausgeliefert wird, sagt die
 * Laufzeit-Konfiguration, und welchen der Nutzer will, sagt das Cookie. Die
 * Werte werden bei jedem Zugriff frisch gelesen statt einmal beim Anlegen —
 * das hält den Dienst ohne zusätzliche Abstraktion testbar.
 *
 * DOCUMENT kommt hier aus @angular/common, nicht aus @angular/core: das
 * Re-Export gibt es erst ab Angular 20, diese Release-Linie fährt Angular 18.
 */
@Injectable({ providedIn: 'root' })
export class UiChannelService {

  private readonly document = inject(DOCUMENT);

  /** Name des Cookies, auf das der Ingress hört. Leer = nicht konfiguriert. */
  get cookieName(): string {
    return environment.uiSwitchCookie ?? '';
  }

  /** Der Kanal, den DIESES Deployment ausliefert. `null` = nicht konfiguriert. */
  get current(): UiChannel | null {
    const value = environment.uiChannel;
    return value === 'stable' || value === 'next' ? value : null;
  }

  /**
   * Der Umschalter existiert nur, wenn beide Werte gesetzt sind. Fehlt die
   * Konfiguration, blendet die Oberfläche ihn aus statt ihn zu deaktivieren.
   */
  get available(): boolean {
    return this.current !== null && this.cookieName !== '';
  }

  /** Der jeweils andere Kanal, oder `null` ohne Konfiguration. */
  get other(): UiChannel | null {
    const current = this.current;
    return current === null || !this.available ? null : otherChannel(current);
  }

  /**
   * Setzt das Cookie und lädt die Seite voll neu.
   *
   * Das Neuladen ist keine Bequemlichkeit: nach dem Wechsel routet der Ingress
   * jede weitere Anfrage auf den anderen Pod. Eine weiterlaufende Angular-Instanz
   * würde ihre nachgeladenen Lazy-Chunks dort anfordern und 404 bekommen.
   */
  switchTo(channel: UiChannel): void {
    if (!this.available) {
      return;
    }
    this.document.cookie = buildCookie(this.cookieName, channel);
    this.document.defaultView?.location.reload();
  }
}
